import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Pagination,
  SelectChangeEvent,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DataTable from "../../common/DataTable";
import { FormData } from "../../types/FormData";
import { useNavigate } from "react-router-dom";
import { fetchCustomers } from "../../api/customerAPI";
import Breadcrumbs from "../../common/Breadcrumbs";
import DateRange from "../../common/filters/DateRange";
import downloadButton from "../../assets/downloadButton.png";

import {
  containerStyle,
  searchAndSortContainer,
  sortByContainer,
  sortByLabelStyle,
  sortBySelectStyle,
  mainContentStyle,
  paginationContainerStyle,
  paginationCaptionStyle,
  customerNameStyle,
  checkDetailsStyles,
  downloadButtonStyles,
} from "./DisplayCustomers.styles";
import moment from "moment";
import { json2csv } from "json-2-csv";
import SearchBar from "../../common/filters/SearchBar";

const DisplayCustomers = () => {
  const [customers, setCustomers] = useState<FormData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<SortTypes>("newest");
  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 8,
    totalRows: 0,
    totalPages: 0,
  });
  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const navigate = useNavigate();
  const firstRender = useRef(true);
  const orgId = localStorage.getItem("orgId");
  const hasMounted = useRef(false);

  // header columns
  const columns = [
    { label: "Name", field: "name" },
    { label: "Created Date", field: "createdAt" },
    { label: "Phone Number", field: "phoneNumber" },
    { label: "Email", field: "email" },
    { label: "Ledger", field: "ledger" },
  ];

  const generateCSV = async () => {
    try {
      const response = await fetchCustomerData("true", searchTerm);

      if (!response?.data) {
        console.error("No data received from API");
        return;
      }

      const allCustomers = response.data.data;

      // Format data for CSV
      const csvData = allCustomers.map((customer: any) => ({
        Name: customer.outletName,
        "Created Date": moment(customer.createdAt).format(
          "DD MMM YY hh:mm:ss A"
        ),
        "Phone Number": customer.phoneNumber || "N/A",
        Email: customer.emailId,
      }));

      // Convert JSON to CSV
      const csv = await json2csv(csvData);

      // Create a Blob and trigger download
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "customers.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  type SortTypes = "newest" | "oldest";

  const sortCustomers = async (customers: FormData[], order: SortTypes) => {
    setSortOrder(order);
  };

  const fetchCustomerData = async (
    download: string,
    search?: string,
    page?: number
  ) => {
    const apiSortOrder = sortOrder === "newest" ? "DESC" : "ASC";

    const adjustedFromDate = from
      ? moment(from).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      : undefined;

    const adjustedToDate = to
      ? moment(to).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      : undefined;

    return await fetchCustomers({
      page: download === "true" ? undefined : page,
      limit: download === "true" ? undefined : pagination.rowsPerPage,
      search,
      startDate: adjustedFromDate,
      endDate: adjustedToDate,
      download,
      sortOrder: apiSortOrder,
    });
  };

  const loadCustomers = async (page: number, search?: string) => {
    try {
      const response = await fetchCustomerData("false", search, page);

      if (response.data.data) {
        setPagination({
          page: response.data.data.pagination.currentPage,
          rowsPerPage: response.data.data.pagination.limit,
          totalRows: response.data.data.pagination.totalRecords,
          totalPages: response.data.data.pagination.totalPages,
        });
        const customerData = response.data.data.customers.map(
          (customer: any) => ({
            ...customer,
            firstName: customer.outletName.split(" ")[0],
            lastName: customer.outletName.split(" ")[1] || "",
            email: customer.emailId,
            createdAt: moment(customer.createdAt).format(
              "DD MMM YY hh:mm:ss A"
            ),
            phoneNumber:
              customer.phoneNumber.length > 0 ? customer.phoneNumber : "N/A",
          })
        );

        setCustomers(customerData);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm.length) {
        loadCustomers(1, searchTerm);
      }
    }, 2000);

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm]);

  useEffect(() => {
    //chnange first render to false
    if (firstRender.current) {
      loadCustomers(1, "");
      firstRender.current = false;
    } else {
      if (hasMounted.current) {
        if ((from && to && sortOrder) || (!from && !to && sortOrder)) {
          loadCustomers(1, searchTerm);
        }
      } else {
        hasMounted.current = true;
      }
    }
  }, [from, to, sortOrder]);

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);

    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle sort order
  const handleSortChange = (e: SelectChangeEvent<string>) => {
    sortCustomers(customers, e.target.value as SortTypes);
  };

  // Handle page change
  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadCustomers(newPage, searchTerm);
  };

  const redirectToLedger = (customerId: string) => {
    navigate(`/${orgId}/seller/ledgers/${customerId}`);
  };

  // Pagination logic
  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + customers.length;
  const currentCustomers = customers.slice(startIndex, endIndex);
  const emptyRows = rowsPerPage - currentCustomers.length;

  // Map data to match column structure
  const tableData = customers.map((customer) => ({
    name: (
      <Typography component="span" sx={customerNameStyle}>
        {`${customer.firstName} ${customer.lastName}`}
      </Typography>
    ),
    createdAt: customer.createdAt,
    phoneNumber: customer.phoneNumber,
    email: customer.email,
    ledger: (
      <button
        onClick={(e) => {
          e.stopPropagation();
          redirectToLedger(customer.id);
        }}
        style={checkDetailsStyles}
      >
        Check Details
      </button>
    ),
    onClick: () => redirectToUser(customer),
  }));

  // Redirect to user details
  const redirectToUser = (customer: any) => {
    navigate(`/${orgId}/seller/customer/${customer.id}`);
  };

  return (
    <>
      <Breadcrumbs
        paths={["All Customers", "Customer Name"]}
        links={["/seller/all-customers", "/seller/all-customers"]}
      />
      {/* Search and Sort */}
      <Box sx={searchAndSortContainer}>
        {/* Search bar */}
        <Box sx={{ flex: 1 }}>
          <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <DateRange
            toDate={to}
            fromDate={from}
            setFromDate={setFrom}
            setToDate={setTo}
          />
          {/* Sort By Dropdown */}
          <Box sx={sortByContainer}>
            <InputLabel id="sort-by-label" sx={sortByLabelStyle}>
              Sort by :
            </InputLabel>
            <Select
              value={sortOrder}
              onChange={handleSortChange}
              IconComponent={KeyboardArrowDownIcon}
              labelId="sort-by-label"
              sx={sortBySelectStyle}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: "16px",
                    marginTop: "10px",
                  },
                },
              }}
            >
              <MenuItem
                value="newest"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Newest
              </MenuItem>
              <MenuItem
                value="oldest"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Oldest
              </MenuItem>
            </Select>
          </Box>
          <img
            style={downloadButtonStyles}
            onClick={generateCSV}
            src={downloadButton}
            alt="download"
          />
        </Box>
      </Box>

      <Box sx={containerStyle}>
        {/* DataTable */}
        <Box sx={mainContentStyle}>
          <DataTable
            columns={columns.map((col) => ({
              ...col,
              labelStyle: {
                fontSize: "14px",
                "&.MuiTableCell-root.MuiTableCell-root": {
                  padding: "18px",
                  width: col.field === "ledger" ? "30%" : "21%",
                },
              },
              cellStyle: {
                fontSize: col.field === "createdAt" ? "12px" : "14px",
                "&.MuiTableCell-root.MuiTableCell-root": { padding: "18px" },
              },
            }))}
            data={tableData}
            emptyStateMessage="No customers available"
            emptyRows={emptyRows}
            onRowClick={(row) => row.onClick()}
          />
        </Box>
        {/* Caption and Pagination */}
        <Box sx={paginationContainerStyle}>
          <Typography variant="subtitle2" sx={paginationCaptionStyle}>
            Showing data {startIndex + 1} to {endIndex} of {totalRows} entries
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => handleChangePage(newPage)}
            shape="rounded"
            variant="outlined"
          />
        </Box>
      </Box>
    </>
  );
};

export default DisplayCustomers;
